/**
 * The Financials tab (§10.3).
 *
 * Statement selector, then a period selector, then the pivot. Rows across
 * periods are matched on `label_raw` because `concept` is NULL on every fact
 * in this database. Where a company's wording changed between filings the
 * rows will not align -- the Corpus page documents this; the tab itself
 * stays uncluttered.
 */
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useLocation, useSearch } from 'wouter'

import type { Pivot } from '@server/sql/facts.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Chip, Label, Skeleton } from '~/components/ui/primitives.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Untranslated } from '~/components/ui/raw.tsx'
import {
  DigitProvider, readDigitMode, storeDigitMode, type DigitMode,
} from '~/features/viewer/digits.tsx'
import { PivotTable } from './pivot-table.tsx'

type Index = {
  statements: { value: string; documents: number; facts: number }[]
  filings: { documentId: number; fiscalYear: number | null; period: string | null; periodEnd: string | null }[]
}
type Response = { index: Index; pivot: Pivot | null }

export function CompanyFinancials({ symbol }: { symbol: string }) {
  const { t, locale, v } = useT()
  const search = useSearch()
  const [location, navigate] = useLocation()

  const sp = new URLSearchParams(search)
  const statement = sp.get('st') ?? ''
  const years = sp.getAll('y').flatMap((y) => y.split(',')).filter(Boolean)
  // The URL wins when it names a mode -- a shared link should show what the
  // sender saw -- and the reader's stored preference decides otherwise.
  const dgParam = sp.get('dg')
  const digits: DigitMode =
    dgParam === 'western' || dgParam === 'printed' ? dgParam : readDigitMode()

  const setParam = useCallback(
    (key: string, values: string[]) => {
      const next = new URLSearchParams(search)
      next.delete(key)
      for (const val of values) next.append(key, val)
      const s = next.toString()
      navigate(`${location}${s ? `?${s}` : ''}`, { replace: true })
    },
    [search, location, navigate],
  )

  const query = useMemo(() => {
    const q = new URLSearchParams()
    if (statement) q.set('statement', statement)
    for (const y of years) q.append('year', y)
    const s = q.toString()
    return s ? `?${s}` : ''
  }, [statement, years])

  const { data, error, refetch } = useQuery({
    queryKey: keys.companyStatements(symbol, query),
    queryFn: () => api<Response>(`/api/companies/${encodeURIComponent(symbol)}/statements${query}`),
    placeholderData: (prev) => prev,
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-96 w-full" />
  if (!data.pivot || data.index.statements.length === 0)
    return <EmptyState title={t('empty.generic')} />

  const availableYears = [
    ...new Set(data.index.filings.map((f) => f.fiscalYear).filter((y): y is number => y !== null)),
  ].sort((a, b) => b - a)

  const active = data.pivot.statement
  const rtl = data.pivot.rows.some((r) => r.labelLang === 'ar')

  return (
    <DigitProvider mode={digits}>
      <div className="space-y-3">
        {/* --- selectors ---------------------------------------------- */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-1.5">
            <Label>{t('fin.statement')}</Label>
            <div className="flex flex-wrap gap-1.5">
              {data.index.statements.map((s) => (
                <Chip
                  key={s.value}
                  selected={s.value === active}
                  count={s.documents}
                  onClick={() => setParam('st', [s.value])}
                >
                  {v('statementType', s.value) ?? <Untranslated>{s.value}</Untranslated>}
                </Chip>
              ))}
            </div>
          </div>

          <Segmented
            size="sm"
            ariaLabel={t('viewer.digits')}
            value={digits}
            onChange={(m) => { storeDigitMode(m); setParam('dg', [m]) }}
            options={[
              { id: 'printed' as const, label: t('viewer.digitsPrinted') },
              { id: 'western' as const, label: t('viewer.digitsWestern') },
            ]}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t('filter.year')}</Label>
          <div className="flex flex-wrap gap-1.5">
            {availableYears.map((y) => (
              <Chip
                key={y}
                selected={years.includes(String(y))}
                onClick={() =>
                  setParam(
                    'y',
                    years.includes(String(y))
                      ? years.filter((x) => x !== String(y))
                      : [...years, String(y)],
                  )
                }
              >
                <span className="tnum">{y}</span>
              </Chip>
            ))}
            {years.length > 0 && (
              <button
                type="button"
                onClick={() => setParam('y', [])}
                className="text-[length:var(--text-micro)] link-inline"
              >
                {t('action.clear')}
              </button>
            )}
          </div>
        </div>

        <div className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {count(data.pivot.rows.length, locale)} × {count(data.pivot.columns.length, locale)}
        </div>

        {data.pivot.columns.length === 0 ? (
          <EmptyState title={t('filter.empty')}>{t('results.emptyHint')}</EmptyState>
        ) : (
          <PivotTable pivot={data.pivot} symbol={symbol} rtl={rtl} />
        )}
      </div>
    </DigitProvider>
  )
}
